import React, { useState } from 'react';
import { Modal } from './ui/Modal';
import { Button } from './ui/Button';
import { useLang } from '../lib/i18n/LanguageContext';
import { dictionary } from '../lib/i18n/dictionary';
import { CheckCircle2Icon } from 'lucide-react';
interface Props {
  open: boolean;
  onClose: () => void;
}
const empty = {
  name: '',
  email: '',
  company: '',
  window: '',
  language: '',
  topic: ''
};
export function ScheduleCallModal({
  open,
  onClose
}: Props) {
  const {
    lang
  } = useLang();
  const t = dictionary.schedule;
  const c = dictionary.common;
  const [form, setForm] = useState(empty);
  const [submitted, setSubmitted] = useState(false);
  const update = (k: keyof typeof empty, v: string) => setForm(p => ({
    ...p,
    [k]: v
  }));
  const windowOptions = lang === 'es' ? ['Mañana (9–12 CT)', 'Tarde (12–15 CT)', 'Final del día (15–18 CT)'] : ['Morning (9–12 CT)', 'Midday (12–15 CT)', 'Late afternoon (15–18 CT)'];
  const langOptions = lang === 'es' ? ['Inglés', 'Español'] : ['English', 'Spanish'];
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // TODO: Connect to Calendly / Cal.com / Supabase / HubSpot — POST payload here
    console.log('[ScheduleCall] payload', form);
    setSubmitted(true);
  };
  const handleClose = () => {
    onClose();
    setTimeout(() => {
      setSubmitted(false);
      setForm(empty);
    }, 250);
  };
  const fieldCls = 'w-full h-11 px-3.5 bg-white border border-slate-200 rounded-sm text-[14px] focus:outline-none focus:border-navy-deep transition-colors';
  const labelCls = 'block text-[13px] font-medium text-navy-deep mb-1.5';
  return <Modal open={open} onClose={handleClose} title={t.title[lang]} data-id="element-199">
      {submitted ? <div className="text-center py-8" data-id="element-200">
          <div className="inline-flex items-center justify-center w-12 h-12 bg-emerald-50 border border-emerald-200 rounded-full mb-4" data-id="element-201">
            <CheckCircle2Icon className="w-5 h-5 text-emerald-600" data-id="element-202" />
          </div>
          <h3 className="font-serif text-xl text-navy-deep mb-2" data-id="element-203">
            {t.successTitle[lang]}
          </h3>
          <p className="text-slate-600 text-sm leading-relaxed" data-id="element-204">
            {t.successBody[lang]}
          </p>
          <Button variant="primary" className="mt-6" onClick={handleClose} data-id="element-205">
            {c.close[lang]}
          </Button>
        </div> : <form onSubmit={handleSubmit} className="space-y-4" data-id="element-206">
          <p className="text-[13.5px] text-slate-600 leading-relaxed" data-id="element-207">
            {t.subtitle[lang]}
          </p>
          <div data-id="element-208">
            <label className={labelCls} data-id="element-209">{t.nameLabel[lang]} *</label>
            <input required className={fieldCls} value={form.name} onChange={e => update('name', e.target.value)} data-id="element-210" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4" data-id="element-211">
            <div data-id="element-212">
              <label className={labelCls} data-id="element-213">{t.emailLabel[lang]} *</label>
              <input required type="email" className={fieldCls} value={form.email} onChange={e => update('email', e.target.value)} data-id="element-214" />
            </div>
            <div data-id="element-215">
              <label className={labelCls} data-id="element-216">{t.companyLabel[lang]}</label>
              <input className={fieldCls} value={form.company} onChange={e => update('company', e.target.value)} data-id="element-217" />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4" data-id="element-218">
            <div data-id="element-219">
              <label className={labelCls} data-id="element-220">{t.windowLabel[lang]}</label>
              <select className={fieldCls} value={form.window} onChange={e => update('window', e.target.value)} data-id="element-221">
                <option value="" data-id="element-222">{c.select[lang]}</option>
                {windowOptions.map(w => <option key={w} value={w} data-id="element-223">
                    {w}
                  </option>)}
              </select>
            </div>
            <div data-id="element-224">
              <label className={labelCls} data-id="element-225">{t.languageLabel[lang]}</label>
              <select className={fieldCls} value={form.language} onChange={e => update('language', e.target.value)} data-id="element-226">
                <option value="" data-id="element-227">{c.select[lang]}</option>
                {langOptions.map(l => <option key={l} value={l} data-id="element-228">
                    {l}
                  </option>)}
              </select>
            </div>
          </div>
          <div data-id="element-229">
            <label className={labelCls} data-id="element-230">{t.topicLabel[lang]}</label>
            <textarea rows={3} className="w-full px-3.5 py-3 bg-white border border-slate-200 rounded-sm text-[14px] focus:outline-none focus:border-navy-deep resize-none" value={form.topic} onChange={e => update('topic', e.target.value)} data-id="element-231" />
          </div>
          <Button type="submit" variant="gold" size="lg" className="w-full" data-id="element-232">
            {t.submit[lang]}
          </Button>
        </form>}
    </Modal>;
}
// Hook + helper to control the modal globally via custom event
export function useScheduleCall() {
  const open = () => window.dispatchEvent(new CustomEvent('aeb:open-schedule'));
  return {
    open
  };
}